<!-- eslint-disable perfectionist/sort-imports -->
<script setup lang="ts">
import { computed } from 'vue';

import ActionButton from '~/components/shared/ActionButton.vue';
import Pagination from '~/components/Pagination';

import type { PartnerViewItem } from '../types';

import { formatNumber, formatPct, isHighPct } from '../utils';

const props = defineProps<{
  currentPage: number;
  hasError?: boolean;
  hasNext: boolean;
  isLoading: boolean;
  pageSize: number;
  rows: PartnerViewItem[];
  totalCount: number;
}>();

const emit = defineEmits<{
  'page-change': [page: number];
  'page-size-change': [pageSize: number];
  view: [partner: PartnerViewItem];
}>();

const startRow = computed(() => (props.currentPage - 1) * props.pageSize + 1);
const endRow = computed(() => Math.min(props.currentPage * props.pageSize, props.totalCount));
</script>

<template>
  <div class="col-12">
    <div class="pv-card">
      <div class="pv-table-wrap">
        <table class="pv-table">
          <thead>
            <tr>
              <th>Parceiro</th>
              <th>Total CCBs elegíveis</th>
              <th>Campanha ativa</th>
              <th>Total revalidados</th>
              <th>% Revalidado</th>
              <th />
            </tr>
          </thead>

          <template v-if="isLoading">
            <tbody>
              <tr v-for="i in 6" :key="i">
                <td v-for="j in 6" :key="j">
                  <div class="placeholder-glow" style="height: 19px">
                    <span class="placeholder col-12" />
                  </div>
                </td>
              </tr>
            </tbody>
          </template>

          <template v-else-if="hasError">
            <tbody>
              <tr>
                <td colspan="6">
                  <div class="pv-state pv-state-error">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#EF4444" stroke-width="2">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="12" />
                      <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    <p>Não foi possível carregar os parceiros. Tente novamente.</p>
                  </div>
                </td>
              </tr>
            </tbody>
          </template>

          <template v-else-if="rows.length === 0">
            <tbody>
              <tr>
                <td colspan="6">
                  <div class="pv-state">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" stroke-width="2">
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                      <circle cx="9" cy="7" r="4" />
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                    </svg>
                    <p>Nenhum parceiro encontrado.</p>
                  </div>
                </td>
              </tr>
            </tbody>
          </template>

          <template v-else>
            <tbody>
              <tr v-for="partner in rows" :key="partner.id">
                <td>
                  <RouterLink
                    class="cell-link"
                    :to="`/revalida-partner-view/${partner.id}`"
                  >
                    {{ partner.name }}
                  </RouterLink>
                  <div class="cell-muted">ID {{ partner.id }}</div>
                </td>
                <td class="cell-strong">{{ formatNumber(partner.eligibleCount) }}</td>
                <td>
                  <span v-if="partner.activeCampaignName" class="badge badge-info">
                    {{ partner.activeCampaignName }}
                  </span>
                  <span v-else class="cell-muted">—</span>
                </td>
                <td class="cell-strong">{{ formatNumber(partner.revalidatedCount) }}</td>
                <td>
                  <strong v-if="isHighPct(partner.revalidatedPct)" class="pct-high">
                    {{ formatPct(partner.revalidatedPct) }}
                  </strong>
                  <span v-else>{{ formatPct(partner.revalidatedPct) }}</span>
                </td>
                <td>
                  <ActionButton type="details" @click="emit('view', partner)" />
                </td>
              </tr>
            </tbody>
          </template>
        </table>
      </div>

      <div v-if="!isLoading && !hasError" class="pv-pagination">
        <span>
          <template v-if="totalCount > 0">
            Mostrando {{ startRow }}–{{ endRow }} de {{ totalCount }} parceiros
          </template>
          <template v-else>
            Nenhum parceiro encontrado
          </template>
        </span>

        <Pagination
          v-if="totalCount > 0"
          class="pv-pagination-controls"
          :current-page="currentPage"
          :has-next="hasNext"
          :page-size="pageSize"
          @page-change="emit('page-change', $event)"
          @page-size-change="emit('page-size-change', $event)"
        />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.pv-card {
  background: #fff;
  border: 1px solid #E4E6EB;
  border-radius: 12px;
  overflow: hidden;
}

.pv-table-wrap {
  overflow-x: auto;
}

.pv-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  thead th {
    background: #FAFAFB;
    color: #6B7280;
    text-align: left;
    padding: 12px 16px;
    font-weight: 600;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border-bottom: 1px solid #E4E6EB;
    white-space: nowrap;
  }

  tbody td {
    padding: 14px 16px;
    border-bottom: 1px solid #E4E6EB;
    vertical-align: middle;
    color: #222;
  }

  tbody tr:hover { background: #FAFBFC; }
  tbody tr:last-child td { border-bottom: none; }
}

.cell-strong {
  font-weight: 600;
  color: #111;
}

.cell-link {
  color: #00B569;
  font-weight: 600;
  text-decoration: none;

  &:hover { text-decoration: underline; }
}

.cell-muted {
  color: #6B7280;
  font-size: 12px;
}

.badge {
  display: inline-flex;
  align-items: center;
  padding: 3px 9px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.3;
}

.badge-info {
  background: #DBEAFE;
  color: #1E40AF;
}

.pct-high {
  color: #00B569;
  font-weight: 700;
}

.pv-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 48px 24px;
  color: #6B7280;
  font-size: 13px;
  text-align: center;

  p { margin: 0; }
}

.pv-state-error p { color: #B91C1C; }

.pv-pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 16px;
  border-top: 1px solid #E4E6EB;
  font-size: 13px;
  color: #6B7280;
  flex-wrap: wrap;
}

.pv-pagination-controls {
  :deep(> .d-flex) {
    margin: 0 !important;
    padding-right: 0 !important;
    width: auto !important;
  }
}
</style>
